/**
 * Release-Info der optionalen Android-App (Paket D, docs/android-app-vertrag.md
 * E11–E13).
 *
 * Zwei Varianten aus einer Codebasis: HANDY (Begehungen, Dashboard,
 * Mitteilungen) und TABLET (voller Umfang wie am PC). Das Release-Skript
 * (scripts/app-release.ts, Paket A) legt je Variante die APK
 * (`sigeko-<variante>-<versionName>-<versionCode>.apk`) und die Release-Info
 * `latest-handy.json` bzw. `latest-tablet.json` in den PRIVATEN Eimer
 * `app-releases` (SQL Nr. 129). Gelesen wird ausschliesslich ueber signierte
 * URLs; das Leserecht prueft Storage am Objekt gegen die Policy
 * `app_releases_select_internal` (angemeldet, kein Freelancer).
 *
 * ─── Fail-open, nie werfen ────────────────────────────────────────────────
 * Fehlt der Eimer (Nr. 129 nicht gefahren), fehlt das Recht (Freelancer),
 * fehlt die Datei (noch nichts veroeffentlicht), ist das Netz weg oder die
 * Release-Info kaputt: jede Funktion hier liefert `null` und wirft nicht.
 * Die Download-Seite zeigt dann „Noch keine App-Version veroeffentlicht",
 * die App meldet kein Update — kein Lade- oder Speicherpfad haengt daran.
 */

import { supabase } from './supabaseClient';
import type { AppVariante } from './nativePlatform';

export const APP_RELEASE_BUCKET = 'app-releases';
/** Gueltigkeit des APK-Links (Vertrag: 10 min). */
export const APK_LINK_GUELTIG_SEKUNDEN = 600;
/** Die Release-Info wird sofort gelesen — eine Minute reicht. */
const INDEX_LINK_GUELTIG_SEKUNDEN = 60;

const istAppVariante = (wert: unknown): wert is AppVariante => wert === 'handy' || wert === 'tablet';

/** Name der Release-Info im Eimer: `latest-handy.json` / `latest-tablet.json`. */
export const releaseIndexDatei = (variante: AppVariante): string => `latest-${variante}.json`;

/** Beschriftung der Varianten fuer Download-Seite und Hinweis. */
export const VARIANTEN_TEXT: Readonly<Record<AppVariante, { titel: string; umfang: string; appName: string }>> = {
    handy: { titel: 'Handy-App', umfang: 'Begehungen, Dashboard, Mitteilungen', appName: 'SiGeKo Smart Report' },
    tablet: { titel: 'Tablet-App', umfang: 'Voller Umfang wie am PC', appName: 'SiGeKo Tablet' },
};

export interface AppRelease {
    variante: AppVariante;
    versionName: string;
    versionCode: number;
    minVersionCode: number;
    file: string;
    sha256: string;
    certSha256: string;
    size: number;
    date: string;
    notes: string;
    /** Download-Hinweis im Android-Browser zeigen (nach dem Pilot, `--hinweis`). Vorgabe false. */
    hinweis: boolean;
}

/**
 * Erlaubte Dateinamen: ein Name ohne Pfad, endet auf `.apk`
 * (`sigeko-handy-1.0.0-1.apk`). Kein `/`, kein `..` — die Release-Info darf
 * auf nichts ausserhalb der Wurzel des Eimers zeigen.
 */
const APK_DATEI = /^[A-Za-z0-9][A-Za-z0-9._-]{0,199}\.apk$/;
/** Traegt der Dateiname eine Variante, muss sie zur Release-Info passen. */
const VARIANTE_IM_DATEINAMEN = /^sigeko-(handy|tablet)-/i;
const HEX64 = /^[0-9a-f]{64}$/;
const ISO_DATUM = /^\d{4}-\d{2}-\d{2}$/;

const ganzeZahl = (wert: unknown, mindestens: number): wert is number =>
    typeof wert === 'number' && Number.isInteger(wert) && wert >= mindestens;

/**
 * SHA-256 als 64 Hex-Zeichen in Kleinschreibung. Doppelpunkte und Leerraum
 * werden entfernt: `apksigner` schreibt den Zertifikats-Fingerabdruck ohne,
 * `keytool` mit Doppelpunkten in Grossschreibung — beides ist derselbe Wert.
 * `null`, wenn danach keine 64 Hex-Zeichen uebrig sind.
 */
export const normalisiereSha256 = (wert: unknown): string | null => {
    if (typeof wert !== 'string') return null;
    const hex = wert.replace(/[\s:]/g, '').toLowerCase();
    return HEX64.test(hex) ? hex : null;
};

/** `YYYY-MM-DD` und ein echter Kalendertag (kein 2026-02-30). */
const istKalenderdatum = (wert: string): boolean => {
    if (!ISO_DATUM.test(wert)) return false;
    const d = new Date(`${wert}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === wert;
};

/** Ist der Dateiname als Objekt dieses Eimers zulaessig? */
export const istApkDateiname = (wert: unknown): wert is string =>
    typeof wert === 'string' && APK_DATEI.test(wert) && !wert.includes('..');

/**
 * Schema-Pruefung einer Release-Info (`latest-<variante>.json`). Alles oder
 * nichts: ein kaputtes Feld macht die ganze Info ungueltig (`null`) — eine
 * halbe Info fuehrte zu einem Download-Knopf ins Leere oder zu einem
 * Sperrbild ohne Grund.
 *
 * `erwartet` ist die Variante, deren Datei gelesen wurde. Eine Info, die sich
 * als ANDERE Variante ausweist (Feld `variante` oder Dateiname
 * `sigeko-<variante>-…`), ist kaputt: sonst installierte ein Handy die
 * Tablet-APK als „Update" — andere appId, also eine zweite App daneben.
 * Fehlt das Feld `variante`, gilt die gelesene Datei.
 *
 * Toleriert werden nur Formfragen: Leerraum um Texte, Doppelpunkte/Gross-
 * schreibung in den Fingerabdruecken, fehlende `notes` (→ '') und fehlendes
 * `hinweis` (→ false). Zusaetzliche Felder werden ignoriert.
 *
 * `minVersionCode > versionCode` ist KAPUTT, nicht streng: jede installierte
 * App, auch die neueste, stuende sonst dauerhaft vor dem Sperrbild.
 */
export const pruefeAppRelease = (roh: unknown, erwartet: AppVariante): AppRelease | null => {
    if (!istAppVariante(erwartet)) return null;
    if (!roh || typeof roh !== 'object' || Array.isArray(roh)) return null;
    const r = roh as Record<string, unknown>;

    if (r.variante !== undefined && r.variante !== null && r.variante !== erwartet) return null;

    const versionName = typeof r.versionName === 'string' ? r.versionName.trim() : '';
    if (!versionName || versionName.length > 40) return null;

    const versionCode = r.versionCode;
    const minVersionCode = r.minVersionCode;
    if (!ganzeZahl(versionCode, 1)) return null;
    if (!ganzeZahl(minVersionCode, 0) || minVersionCode > versionCode) return null;

    const file = typeof r.file === 'string' ? r.file.trim() : '';
    if (!istApkDateiname(file)) return null;
    const imNamen = VARIANTE_IM_DATEINAMEN.exec(file);
    if (imNamen && imNamen[1].toLowerCase() !== erwartet) return null;

    const sha256 = normalisiereSha256(r.sha256);
    const certSha256 = normalisiereSha256(r.certSha256);
    if (!sha256 || !certSha256) return null;

    const size = r.size;
    if (!ganzeZahl(size, 0)) return null;

    const date = typeof r.date === 'string' ? r.date.trim() : '';
    if (!istKalenderdatum(date)) return null;

    if (r.notes !== undefined && r.notes !== null && typeof r.notes !== 'string') return null;
    const notes = typeof r.notes === 'string' ? r.notes.trim() : '';

    if (r.hinweis !== undefined && r.hinweis !== null && typeof r.hinweis !== 'boolean') return null;
    const hinweis = r.hinweis === true;

    return {
        variante: erwartet, versionName, versionCode, minVersionCode, file, sha256, certSha256, size, date, notes, hinweis,
    };
};

/**
 * Release-Info EINER Variante aus dem Eimer lesen und pruefen.
 *
 * Ueber eine signierte URL plus `fetch(…, { cache: 'no-store' })` statt
 * `storage.download()`: der Download-Weg von supabase-js laesst sich nicht vom
 * Browser-Zwischenspeicher ausnehmen, und Storage liefert Objekte mit dem
 * `cache-control` des Uploads aus (Standard eine Stunde). Eine zwischen-
 * gespeicherte Info hielte Update-Hinweis und Sperrbild bis zu eine Stunde
 * zurueck.
 */
export async function ladeAppRelease(variante: AppVariante): Promise<AppRelease | null> {
    try {
        if (!istAppVariante(variante)) return null;
        const { data, error } = await supabase.storage
            .from(APP_RELEASE_BUCKET)
            .createSignedUrl(releaseIndexDatei(variante), INDEX_LINK_GUELTIG_SEKUNDEN);
        const url: unknown = data?.signedUrl;
        if (error || typeof url !== 'string' || !url) return null;
        const antwort = await fetch(url, { cache: 'no-store' });
        if (!antwort.ok) return null;
        const text = await antwort.text();
        let roh: unknown;
        try {
            roh = JSON.parse(text);
        } catch {
            return null;
        }
        return pruefeAppRelease(roh, variante);
    } catch {
        return null;
    }
}

/** Beide Varianten auf einmal (Download-Seite, Hinweis). Wirft nie. */
export async function ladeAlleAppReleases(): Promise<Record<AppVariante, AppRelease | null>> {
    const [handy, tablet] = await Promise.all([ladeAppRelease('handy'), ladeAppRelease('tablet')]);
    return { handy, tablet };
}

/**
 * Kurzlebiger Download-Link fuer die APK (600 s). `download` setzt
 * `Content-Disposition: attachment` mit dem Dateinamen — der Browser laedt
 * herunter, statt die APK als Seite anzuzeigen.
 */
export async function apkDownloadLink(release: AppRelease): Promise<string | null> {
    try {
        const datei = release?.file;
        if (!istApkDateiname(datei)) return null;
        const { data, error } = await supabase.storage
            .from(APP_RELEASE_BUCKET)
            .createSignedUrl(datei, APK_LINK_GUELTIG_SEKUNDEN, { download: datei });
        const url: unknown = data?.signedUrl;
        if (error || typeof url !== 'string' || !url) return null;
        return url;
    } catch {
        return null;
    }
}

/**
 * Welche Variante passt zu diesem Bildschirm? Kuerzere Seite ab 600 CSS-px →
 * Tablet (Vertrag E13). Unbrauchbare Masse → Handy.
 */
export const vorgeschlageneVariante = (breite: number, hoehe: number): AppVariante => {
    const kurz = Math.min(breite, hoehe);
    return Number.isFinite(kurz) && kurz >= 600 ? 'tablet' : 'handy';
};

/** Vorschlag fuer DIESES Geraet (`window.screen`, in CSS-Pixeln). */
export const geraeteVorschlag = (): AppVariante => {
    try {
        return vorgeschlageneVariante(window.screen.width, window.screen.height);
    } catch {
        return 'handy';
    }
};

/**
 * Adresse der Download-Seite (`/#/app`) — Ziel des QR-Codes und der Links.
 * `pathname` bleibt drin, weil die App nicht zwingend an der Wurzel liegt
 * (Muster `freimeldungLinkUrl`).
 */
export const appDownloadSeitenUrl = (origin: string, pathname: string): string =>
    `${origin}${pathname || '/'}#/app`;

export type DownloadZugang = 'intern' | 'freelancer' | 'abgemeldet';

export interface NutzerZugang {
    zugang: DownloadZugang;
}

/**
 * Wer schaut? Den Download gibt es nur fuer interne Nutzer (E3).
 *
 * Freelancer erkennt der Client an `profiles.role === 'freelancer'` (wie
 * App.tsx vor dem Freelancer-Portal). Ist das Profil nicht lesbar, gilt der
 * Nutzer hier als intern: die Entscheidung faellt ohnehin am Server (RLS am
 * Eimer), und ein Lesefehler soll keinem Mitarbeiter die Seite versperren.
 */
export async function ladeDownloadZugang(): Promise<NutzerZugang> {
    try {
        const { data } = await supabase.auth.getSession();
        const uid: unknown = data?.session?.user?.id;
        if (typeof uid !== 'string' || !uid) return { zugang: 'abgemeldet' };
        const { data: profil, error } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', uid)
            .maybeSingle();
        const rolle: unknown = error ? null : profil?.role;
        if (rolle === 'freelancer') return { zugang: 'freelancer' };
        return { zugang: 'intern' };
    } catch {
        return { zugang: 'abgemeldet' };
    }
}

// ─── Stand je Sitzung (Hinweisleiste, Menuepunkt) ───────────────────────────
//
// Hinweisleiste und Menuepunkt fragen bei jedem Einhaengen — und die Leiste
// wird bei jedem Projekt-Oeffnen neu eingehaengt (der Ladeschirm baut den Baum
// ab). Ohne Zwischenspeicher liefen dabei jedes Mal Profil-Abfrage, zwei
// signierte URLs und zwei JSON-Abrufe. Deshalb EINMAL je Anmeldung:
//   • Schluessel ist die Nutzer-Id aus `auth.getSession()` (lokal gelesen,
//     kein Netz). Die App laedt beim Abmelden NICHT neu — meldet sich im
//     selben Tab ein anderer an, gilt sein eigener Stand.
//   • Ein Stand OHNE lesbare Release-Info (bei internem Nutzer) haelt nur
//     10 Minuten: ein Funkloch beim ersten Abruf soll Leiste und Menuepunkt
//     nicht fuer die ganze Sitzung verstecken, und eine frisch
//     veroeffentlichte Version erscheint ohne Neuladen.
// Die Update-Pruefung (appUpdate) und die Download-Seite lesen weiter frisch.

export interface SitzungsStand {
    zugang: NutzerZugang;
    releases: Record<AppVariante, AppRelease | null>;
}

const OHNE_STAND: SitzungsStand = {
    zugang: { zugang: 'abgemeldet' },
    releases: { handy: null, tablet: null },
};

/** Wie lange ein Stand ohne lesbare Release-Info gilt. */
export const LEERER_STAND_GUELTIG_MS = 10 * 60 * 1000;

let sitzungsStand: { uid: string; seit: number; stand: Promise<SitzungsStand> } | null = null;

const aktuelleNutzerId = async (): Promise<string | null> => {
    const { data } = await supabase.auth.getSession();
    const uid: unknown = data?.session?.user?.id;
    return typeof uid === 'string' && uid ? uid : null;
};

const standOhneVersion = (s: SitzungsStand): boolean =>
    s.zugang.zugang === 'intern' && !s.releases.handy && !s.releases.tablet;

/**
 * Zugang und beide Release-Infos, zwischengespeichert je Anmeldung (siehe
 * oben). Gleichzeitige Aufrufe teilen einen Abruf. Wirft nie.
 */
export async function ladeSitzungsStand(jetzt: () => number = Date.now): Promise<SitzungsStand> {
    try {
        const uid = await aktuelleNutzerId();
        if (!uid) return OHNE_STAND;
        const t = jetzt();
        const vorhanden = sitzungsStand;
        if (vorhanden && vorhanden.uid === uid) {
            const stand = await vorhanden.stand;
            if (!standOhneVersion(stand) || t - vorhanden.seit < LEERER_STAND_GUELTIG_MS) return stand;
            // Nur ersetzen, wenn inzwischen niemand anderes neu geladen hat.
            if (sitzungsStand !== vorhanden) return ladeSitzungsStand(jetzt);
        }
        const stand = (async (): Promise<SitzungsStand> => {
            const zugang = await ladeDownloadZugang();
            const releases = zugang.zugang === 'intern' ? await ladeAlleAppReleases() : OHNE_STAND.releases;
            return { zugang, releases };
        })();
        sitzungsStand = { uid, seit: t, stand };
        return await stand;
    } catch {
        return OHNE_STAND;
    }
}

/**
 * Ist irgendeine App-Variante fuer diesen Nutzer lesbar veroeffentlicht?
 * Fuer den Menuepunkt „App" (Paket C). Je Sitzung zwischengespeichert, wirft
 * nie — im Zweifel `false`.
 */
export async function appReleaseVorhanden(): Promise<boolean> {
    try {
        const { releases } = await ladeSitzungsStand();
        return !!(releases.handy || releases.tablet);
    } catch {
        return false;
    }
}

/** Nur fuer Tests: Zwischenspeicher leeren. */
export const __setzeSitzungsStandZurueck = (): void => { sitzungsStand = null; };

// ─── Anzeige ────────────────────────────────────────────────────────────────

/** Bytes als „7,0 MB" (deutsch, eine Nachkommastelle ab 1 MB). */
export const formatiereGroesse = (bytes: number): string => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '–';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MB`;
    const kb = Math.max(1, Math.round(bytes / 1024));
    return `${kb.toLocaleString('de-DE')} KB`;
};

/** `2026-10-15` → `15.10.2026`; alles andere unveraendert. */
export const formatiereDatum = (iso: string): string => {
    const t = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
    return t ? `${t[3]}.${t[2]}.${t[1]}` : iso;
};

/** Zertifikats-Fingerabdruck wie `keytool` ihn zeigt: `AB:CD:…`. */
export const alsFingerabdruck = (hex: string): string =>
    (hex.toUpperCase().match(/.{1,2}/g) ?? []).join(':');
